import { useCallback } from "react";
import { useTranslation } from "Shared/util/custom-hooks";
import { Nothing, PaymentInformationMessageResource } from "Shared/util/types";
import {
  BIC_HANDELSBANKEN,
  BIC_SWEDBANK,
  StatusMessageTranslation,
} from "../types";

const formatTranslationLink = (translationKey: string) =>
  `${translationKey}_LINK`;

const formatTranslationLinkText = (translationKey: string) =>
  `${translationKey}_LINK_TEXT`;

const useGetBicTranslationOverride = () => {
  const tHandsess = useTranslation(
    (x) => x.edit_payment_information.messageCodes.bicOverrides.handsess
  );
  const tHSwedsess = useTranslation(
    (x) => x.edit_payment_information.messageCodes.bicOverrides.swedsess
  );

  return useCallback(
    (
      bic: string | Nothing,
      translationKey: PaymentInformationMessageResource["translationKey"]
    ): StatusMessageTranslation | null => {
      switch (bic) {
        case BIC_HANDELSBANKEN:
          return {
            link: tHandsess.findByKey(formatTranslationLink(translationKey)),
            linkText: tHandsess.findByKey(
              formatTranslationLinkText(translationKey)
            ),
            text: tHandsess.findByKey(translationKey),
          };

        case BIC_SWEDBANK:
          return {
            link: tHSwedsess.findByKey(formatTranslationLink(translationKey)),
            linkText: tHSwedsess.findByKey(
              formatTranslationLinkText(translationKey)
            ),
            text: tHSwedsess.findByKey(translationKey),
          };

        default:
          return null;
      }
    },
    [tHandsess, tHSwedsess]
  );
};

const useGetTranslation = () => {
  const tg = useTranslation((x) => x.global);
  const t = useTranslation((x) => x.edit_payment_information.messageCodes);

  return useCallback(
    (
      translationKey: PaymentInformationMessageResource["translationKey"]
    ): string =>
      !translationKey ? tg.thereWasTechnicalError : t.findByKey(translationKey),
    [t, tg]
  );
};

const isBicTranslationOverrideValid = (
  translation: StatusMessageTranslation | null
): boolean =>
  !!translation?.text || (!!translation?.link && !!translation?.linkText);

export const useGetMessageCodeTranslation = () => {
  const getBicTranslationOverride = useGetBicTranslationOverride();
  const getTranslation = useGetTranslation();

  return useCallback(
    (
      bic: string | Nothing,
      translationKey: PaymentInformationMessageResource["translationKey"]
    ): StatusMessageTranslation => {
      const bicTranslationOverride = getBicTranslationOverride(
        bic,
        translationKey
      );

      const shouldUseBicTranslationOverride = isBicTranslationOverrideValid(
        bicTranslationOverride
      );

      const output =
        bicTranslationOverride && shouldUseBicTranslationOverride
          ? bicTranslationOverride
          : {
              text: getTranslation(translationKey) || translationKey,
            };

      return output;
    },
    [getBicTranslationOverride, getTranslation]
  );
};
